'use client'

import { LuneTheme, useDevice } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import Image from 'next/image'
import { FC, ReactNode } from 'react'

interface PlugInAndGoBoxProps {
    backgroundImageUrl: string
    title: ReactNode
    description: ReactNode
}

const PlugInAndGoBox: FC<{ item: PlugInAndGoBoxProps }> = ({ item }) => {
    const { palette } = LuneTheme
    const { backgroundImageUrl, title, description } = item
    const { xsScreen, smScreen, mdScreen } = useDevice()

    return (
        <Box
            sx={{
                backgroundColor: palette.Grey300,
                minWidth: '380px',
                maxWidth: xsScreen ? '400px' : mdScreen ? '300px' : '400px',
                position: 'relative',
                flex: smScreen ? '' : '1 1 auto',
                width: smScreen ? '100%' : 'calc(33.33% - 24px)',
                borderRadius: '32px',
                overflow: 'hidden',
                img: {
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '100%',
                    height: '100%',
                },
            }}
        >
            <Image src={backgroundImageUrl} width={348} height={348} alt={`${title}-image`} />
            <Box
                sx={{
                    paddingBottom: '100%',
                    height: '0',
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '24px',
                        left: '24px',
                        right: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    <Text variant={'h6'}>{title}</Text>
                    <Text variant={'body2'} sx={{ textAlign: 'center' }}>
                        {description}
                    </Text>
                </Box>
            </Box>
        </Box>
    )
}

const PlugInAndGo: FC<{
    items: PlugInAndGoBoxProps[]
    title?: string
    body?: ReactNode
}> = ({ items, title = 'How to get started', body }) => {
    const { mdScreen } = useDevice()

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: body || mdScreen ? 6 : 9,
            }}
        >
            <Box>
                <Text
                    variant={mdScreen ? 'h5' : 'h4'}
                    sx={{ mb: body ? 3 : 0, textAlign: 'center' }}
                >
                    {title}
                </Text>
                {body ? (
                    <Text variant={mdScreen ? 'body2' : 'body1'} sx={{ textAlign: 'center' }}>
                        {body}
                    </Text>
                ) : null}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: mdScreen ? 2 : 3,
                    flexDirection: 'row',
                    width: '100%',
                }}
            >
                {items.map((item, index) => (
                    <PlugInAndGoBox item={item} key={`plug-and-go-${index}`} />
                ))}
            </Box>
        </Box>
    )
}

export default PlugInAndGo
